
import { useState, useEffect, useRef } from "react";
import { Product } from "@/lib/types";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

export interface ProductQuantity {
  productId: string;
  quantity: number;
}

interface GroupProductQuantitiesProps {
  products: Product[];
  selectedProductIds: string[];
  initialQuantities?: ProductQuantity[];
  onChange: (quantities: ProductQuantity[]) => void;
}

const GroupProductQuantities = ({
  products,
  selectedProductIds,
  initialQuantities = [],
  onChange
}: GroupProductQuantitiesProps) => {
  const [quantities, setQuantities] = useState<ProductQuantity[]>(initialQuantities);
  const isInitialRender = useRef(true);
  const previousSelectedIds = useRef<string[]>([]);
  const hasInitialQuantitiesChanged = useRef(false);

  useEffect(() => {
    if (
      JSON.stringify(previousSelectedIds.current) === JSON.stringify(selectedProductIds) && 
      !isInitialRender.current && 
      !hasInitialQuantitiesChanged.current
    ) {
      return; // Skip if selected products haven't changed (except on initial render)
    }

    console.log("Updating quantities due to selected products change");
    
    const existingQuantities = new Map<string, number>();
    
    initialQuantities.forEach(q => existingQuantities.set(q.productId, q.quantity));
    
    quantities.forEach(q => {
      if (!existingQuantities.has(q.productId)) {
        existingQuantities.set(q.productId, q.quantity);
      }
    });
    
    const updatedQuantities = selectedProductIds.map(productId => ({
      productId,
      quantity: existingQuantities.get(productId) || 1
    }));
    
    if (JSON.stringify(updatedQuantities) !== JSON.stringify(quantities)) {
      console.log("Setting new quantities:", updatedQuantities);
      setQuantities(updatedQuantities);
      
      if (!isInitialRender.current) {
        onChange(updatedQuantities);
      }
    }
    
    previousSelectedIds.current = [...selectedProductIds];
    isInitialRender.current = false;
    hasInitialQuantitiesChanged.current = false;
  }, [selectedProductIds, initialQuantities, onChange]);

  useEffect(() => {
    if (initialQuantities.length > 0 && !isInitialRender.current) {
      console.log("Initial quantities changed externally:", initialQuantities);
      hasInitialQuantitiesChanged.current = true;
      setQuantities(initialQuantities);
    }
  }, [initialQuantities]);

  const handleQuantityChange = (productId: string, quantity: number) => {
    const updatedQuantities = quantities.map(q => 
      q.productId === productId ? { ...q, quantity } : q
    );
    
    console.log("Quantity changed:", productId, quantity);
    setQuantities(updatedQuantities);
    onChange(updatedQuantities);
  };

  // Handle direct input focus
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  if (selectedProductIds.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <Label>Product Quantities</Label>
      <div className="space-y-2">
        {selectedProductIds.map(productId => {
          const product = products.find(p => p.id === productId);
          const quantityItem = quantities.find(q => q.productId === productId);
          const quantity = quantityItem ? quantityItem.quantity : 1;
          
          if (!product) return null;
          
          return (
            <div key={productId} className="flex items-center space-x-2">
              <span className="flex-grow">{product.name}</span>
              <div className="w-24">
                <Input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={e => handleQuantityChange(productId, Math.max(1, parseInt(e.target.value) || 1))}
                  onFocus={handleFocus}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GroupProductQuantities;
